import { Component } from 'react'
import { toast } from 'react-toastify'
import { IClientUID } from '../../../api/clients'
import { getString, getLocale } from '../../../locale'
import Loader from '../../../components/loader'
import { getClientSystemInfo } from '../../../api/0x0001/systeminfo'
import { updateClientDateTime } from '../../../api/0x0001/datetime'
import Flatpickr from 'react-flatpickr'
import { Hebrew } from 'flatpickr/dist/l10n/he'
import { english as English } from 'flatpickr/dist/l10n/default'
import { getClientFragmentedSystemInfo, IFragmentedSystemInfo } from '../../../api/0x0002/fragmented-systeminfo'

type Props = {
    clientId: IClientUID
}

type State = {
    dateTime?: Date
    working: boolean
}

class DateTimeTab extends Component<Props, State> {
    state: State = {
        working: false,
    }

    componentDidMount = () => {
        this.loadDateTime()
    }

    findFragment = (systemInfo: IFragmentedSystemInfo, prop: keyof IFragmentedSystemInfo[number]): IFragmentedSystemInfo[number][keyof IFragmentedSystemInfo[number]] | undefined => {
        if (systemInfo === undefined) return undefined
        for (const fragmentVersion in systemInfo) {
            const fragment = systemInfo[fragmentVersion]
            if (fragment === undefined) continue
            if (prop === 'common') {
                const maybeCommon = fragment[prop] as { epoch?: number; uptime?: number; freeHeap?: number; ambient?: number } | undefined
                if (
                    maybeCommon !== undefined &&
                    maybeCommon.epoch !== undefined &&
                    maybeCommon.uptime !== undefined &&
                    maybeCommon.freeHeap !== undefined &&
                    maybeCommon.ambient !== undefined
                ) {
                    return fragment[prop]
                }
                continue
            }
            if (fragment[prop] !== undefined) {
                return fragment[prop]
            }
        }
        return undefined
    }

    async loadDateTime() {
        try {
            const systemInfo = (await getClientFragmentedSystemInfo(this.props.clientId)).data
            // console.log('systemInfo', systemInfo)
            const commonFragment = this.findFragment(systemInfo, 'common') as IFragmentedSystemInfo[number]['common']
            if (commonFragment === undefined || commonFragment.epoch === undefined) {
                throw new Error('Failed to parse common system info')
            }
            // console.log('commonFragment', commonFragment)
            this.setState({ dateTime: new Date(Number(commonFragment.epoch) * 1000) })
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    async saveDateTime() {
        try {
            const { dateTime } = this.state
            if (dateTime === undefined) return
            this.setState({ working: true })
            await updateClientDateTime(this.props.clientId, Math.floor(dateTime.getTime() / 1000))
            setTimeout(() => {
                this.loadDateTime()
                this.setState({ working: false })
            }, 1000)
        } catch (err) {
            this.setState({ working: false })
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    async syncWithLocal() {
        try {
            this.setState({ working: true })
            await updateClientDateTime(this.props.clientId, Math.floor(Date.now() / 1000))
            setTimeout(() => {
                this.loadDateTime()
                this.setState({ working: false })
            }, 1000)
        } catch (err) {
            this.setState({ working: false })
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    render() {
        if (this.state.dateTime === undefined) return <Loader size={14} />
        return (
            <div>
                {this.renderActions()}
                {this.renderDateTimePicker()}
            </div>
        )
    }

    renderActions() {
        return (
            <div className="grid grid-cols-4 gap-5">
                {this.renderActionButton(getString('settings.refresh'), this.loadDateTime.bind(this))}
                {this.renderActionButton(getString('common.save'), this.saveDateTime.bind(this))}
                {this.renderActionButton(getString('datetime.sync_with_local'), this.syncWithLocal.bind(this))}
            </div>
        )
    }

    renderActionButton(label: string, onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) {
        const { working } = this.state
        return (
            <button
                onClick={onClick}
                className="group py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-501"
                disabled={working}
            >
                {label}
            </button>
        )
    }

    renderDateTimePicker() {
        const { dateTime } = this.state
        console.log('dateTime', dateTime)
        return (
            <div className="mt-5">
                <Flatpickr
                    className="pretty-input"
                    data-enable-time
                    value={dateTime}
                    onChange={dateTime => this.setState({ dateTime: dateTime[0] })}
                    options={{
                        locale: getLocale() === 'he' ? Hebrew : English,
                    }}
                />
            </div>
        )
    }
}

export default DateTimeTab
